import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Activity,
  Brain,
  Heart,
  AlertTriangle,
  Sparkles,
  Loader2,
  Droplet,
  Wind,
  Shield
} from 'lucide-react';

import { API_URL } from '../config';

export default function ReferentielsInfectiologiePage() {
  const [aiLoading, setAiLoading] = useState({});
  const [aiResults, setAiResults] = useState({});

  const handleAiAnalysis = async (infectionId, infectionName, content) => {
    setAiLoading({ ...aiLoading, [infectionId]: true });
    
    try {
      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: 'referentiels_infectiologie',
          audience: 'adult',
          prompt: `Analyse infectiologique pour ${infectionName}:

${content.slice(0, 500)}

Fournis une analyse synthétique avec:
1. Reconnaissance des signes de gravité
2. Antibiothérapie probabiliste immédiate
3. Mesures de réanimation associées
4. Critères d'orientation hospitalière
5. Précautions et surveillance`
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResults({ ...aiResults, [infectionId]: data.response });
        toast.success(`Analyse AI ${infectionName} complétée`);
      } else {
        toast.error('Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setAiLoading({ ...aiLoading, [infectionId]: false });
    }
  };

  const protocols = [
    {
      id: 'choc-septique',
      name: 'Choc Septique de l\'Adulte',
      icon: Droplet,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**CHOC SEPTIQUE DE L'ADULTE**

**DIAGNOSTIC**

**Sepsis - Utilisation du qSOFA (Quick SOFA):**
• Pression artérielle systolique ≤ 100 mm Hg
• Fréquence respiratoire ≥ 22/mn
• Confusion

Si ≥ 2 items en contexte de suspicion d'infection = sepsis = monitoring rapproché

**Choc septique:**
• Sepsis présent
• Besoin de drogues vasopressives pour maintenir une PAM ≥ 65 mm Hg
• Lactates > 2 mmol/l malgré un remplissage adéquat

**ÉLÉMENTS DE GRAVITÉ**

• Nombre de défaillances d'organe associées
• Purpura fébrile: Antibiotique immédiat (cf protocole purpura fulminans)

**CONDUITE À TENIR**

**Mesures initiales:**
• Deux VVP au minimum
• Scope monitoring
• Position Trendelenburg
• Prélèvements infectieux (hémocultures, ECBU…) + lactates
• Oxygénothérapie

**EXPANSION VOLÉMIQUE:**
• **Sérum salé NaCl 0,9%**
• **Au moins 30 mL/kg dans les 3 premières heures**
• Surveiller éventuels signes cliniques (+/- échographiques) d'OAP

**AMINES VASOPRESSIVES:**
Introduites précocement si échec du remplissage (généralement 30mL/Kg)

• **Noradrénaline 0,5 à 2 mg/h IVSE**
• Augmenter par paliers de 0,2 à 0,5 mg/h toutes les 15 min si non efficace
• Sur VVP si diluée: 8 mg / 40 cc soit 0,2 mg/mL

**INTUBATION:**
IOT si troubles de la conscience et/ou détresse respiratoire persistante

**ANTIBIOTHÉRAPIE PROBABILISTE**
**Double, précoce, selon porte d'entrée**
**⏱ OBJECTIF DANS L'HEURE**

**Sans point d'appel - Communautaire:**
• **Ceftriaxone 2 g/jour IV**
  +
• **Gentamycine 7 mg/kg** [1ère dose sur 30 min IV lente dans 250 cc]

**Sans point d'appel - Nosocomial:**
(Si ≥ 2 facteurs: C3G/FQ/Tazo dans les 3 mois, BLSE/P.aeruginosa dans les 3 mois, hospitalisation étranger, EHPAD avec sonde/gastrostomie, épidémie BMR)

• **Amikacine 25-30 mg/kg** une seule dose IVL sur 30 min
  +
• **Vancomycine 15 mg/kg x 2/j** IVL sur une heure
  +
• **Piperacilline-tazobactam 4 g** IVL 60 min
  OU **Imipénème 1g** IVL sur 60 min
  OU **Ceftazidime 2g** IVL 60min + **Métronidazole 500 mg/8h** en 30 min

**Pneumopathie communautaire:**
• **Ceftriaxone 2 g/jour**
  +
• **Spiramycine 1,5 M UI/8h**
  OU **Ofloxacine 200 mg × 2** (si absence de macrolide utilisable)
• Ajouter **Linézolide** ou **Dalacine** en contexte post-grippal ou si images de nécrose

**Pneumopathie à P. Aeruginosa suspectée:**
Si antériorité connue de colonisation:
• **Tazocilline 4g/6h**
  +
• **Amikacine 25-30 mg/kg/j** (IVL sur 30 min, une dose)

**Infection urinaire:**
• **Ceftriaxone 2 g/jour**
  +
• **Amikacine 25-30 mg/kg/j** (IVL 30 min en 1 dose)

**Infection d'origine abdominale:**
(Communautaire, nosocomiale ou post-opératoire)
• **Ceftriaxone 2 g/j**
  +
• **Métronidazole 500 mg/8h**
  +
• **Amikacine 25-30 mg/kg/j**

**Infection abdominale - Patient porteur BLSE:**
• **Imipénème 1G** IVL 60 min
  +
• **Amikacine 25-30 mg/kg/j** (uniquement si gravité)

**OBJECTIFS THÉRAPEUTIQUES**

• SatO2 ≥ 95%
• PAM ≥ 65 mmHg
• Diurèse > 0,5 mg/kg/h (sondage urinaire dès que possible)
• Hb > 7 g/dL
• Glycémie 1,4-1,8 g/L (insuline IVSE si 2 mesures > 1,8 g/L)

**TRANSPORT**

• Médicalisé vers Réanimation

**À SAVOIR**

• Dose initiale d'aminoside indépendante de la fonction rénale
• Utilisation de l'imipénème en contexte nosocomial si ≥ 2 facteurs de risque
• Hémocultures AVANT antibiothérapie si possible sans retard

**DÉFINITION:**
Dysfonction d'organe potentiellement mortelle causée par une réponse inappropriée de l'hôte à une infection.

**CRITÈRES DIAGNOSTIQUES (SOFA ≥ 2):**
• Hypotension persistante (PAS < 90 mmHg)
• Besoin de vasopresseurs pour maintenir PAM ≥ 65 mmHg
• Lactate > 2 mmol/L malgré remplissage vasculaire adéquat

**SIGNES CLINIQUES:**
• **Hémodynamiques:**
  - Tachycardie > 90 bpm
  - Hypotension artérielle
  - Marbrures cutanées
  - TRC allongé > 3 sec
  - Oligurie < 0,5 mL/kg/h

• **Respiratoires:**
  - Tachypnée > 22/min
  - SpO2 < 90%
  - Dyspnée

• **Neurologiques:**
  - Confusion, agitation
  - Glasgow < 15

• **Métaboliques:**
  - Hyperlactatémie
  - Fièvre > 38°C ou hypothermie < 36°C

**TRAITEMENT PRÉ-HOSPITALIER:**

**1. RÉANIMATION HÉMODYNAMIQUE:**

**Remplissage vasculaire (30 mL/kg en 30 min):**
• **NaCl 0,9%** ou **Ringer Lactate**
• 500-1000 mL en bolus rapide
• Réévaluation après chaque 500 mL

**Critères de réponse au remplissage:**
• Augmentation PAS > 10 mmHg
• Diminution FC > 10 bpm
• Amélioration marbrures
• Diurèse > 0,5 mL/kg/h

**2. VASOPRESSEURS (si hypotension persistante):**

• **NORADRÉNALINE (1ère intention):**
  - Ampoule: 8 mg/4 mL
  - Dilution: 4 mg dans 50 mL (0,08 mg/mL)
  - Posologie: 0,1-1 µg/kg/min
  - Objectif: PAM ≥ 65 mmHg
  - Voie: Intraveineuse stricte (VVC préférable)

**3. ANTIBIOTHÉRAPIE (< 1 heure):**

**Foyer indéterminé:**
• **Ceftriaxone 2g IV** + **Gentamicine 5 mg/kg IV**

**Foyer pulmonaire:**
• **Ceftriaxone 2g IV** + **Spiramycine 3 MUI IV**

**Foyer abdominal:**
• **Ceftriaxone 2g IV** + **Métronidazole 500 mg IV**

**Foyer urinaire:**
• **Ceftriaxone 2g IV**

**Suspicion méningée:**
• **Ceftriaxone 2g IV** (ne pas retarder pour PL)

**4. OXYGÉNOTHÉRAPIE:**
• Objectif: SpO2 > 94%
• Masque haute concentration si besoin
• Intubation si détresse respiratoire sévère

**5. SURVEILLANCE:**
• PA continue (toutes les 5 min)
• FC, SpO2 continu
• Température
• État de conscience
• Diurèse
• Glycémie capillaire

**6. MESURES ASSOCIÉES:**
• **Contrôle glycémique:** Objectif 6-10 mmol/L
• **Protection gastrique:** Si ventilation mécanique
• **Prévention thrombose:** Pas en pré-hospitalier

**CRITÈRES D'ORIENTATION:**
• **Réanimation SYSTÉMATIQUE**
• Contact réanimateur avant transport
• Scope monitoring durant transport

**POINTS CLÉS:**
✓ Antibiothérapie AVANT 1 heure (golden hour)
✓ Remplissage vasculaire agressif précoce
✓ Noradrénaline si hypotension persistante
✓ Source septique à identifier et traiter
✓ Transport médicalisé obligatoire`
    },
    {
      id: 'meningite',
      name: 'Méningite Bactérienne de l\'Adulte',
      icon: Brain,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      border: 'border-purple-400/30',
      content: `**MÉNINGITE BACTÉRIENNE DE L'ADULTE**

**DIAGNOSTIC**

**Triade classique (sensibilité ≈ 45%):**
• Fièvre
• Raideur de nuque
• Altération de la conscience (Glasgow < 14)

**Signes cliniques:**
• Céphalées
• Raideur de nuque
• Fièvre
• Altération de la conscience
• Signes cutanés (purpura): évoque méningocoque (90%)
• Signes de Kernig et Brudzinski: mauvaise sensibilité

**CONDUITE À TENIR**

**Mesures d'isolement:**
• Précautions complémentaires de type gouttelettes
• Masque FFP2 si IOT

**ANTIBIOTIQUES AVANT PL**
**(EN SMUR COMME AU SAU)**

**Indications d'ATB avant PL:**
• Purpura fulminans (cf protocole purpura fulminans)
• Prise en charge hospitalière ne pouvant être réalisée dans les 90 min
• **Contre-indications à la réalisation de la PL:**
  - Anomalie connue de l'hémostase, plaquettes < 50 000
  - Traitement anticoagulant efficace dont AOD (pas de CI si AAP)
  - Suspicion trouble majeur de l'hémostase ou saignement actif
  - Risque élevé d'engagement cérébral
  - Instabilité hémodynamique

**ANTIBIOTHÉRAPIE D'URGENCE:**

Si indication d'ATB avant PL:
• **Céfotaxime 75 mg/kg** 
  OU **Ceftriaxone 50 mg/kg** (max 4g)
  +
• **Dexaméthasone 10 mg IV**

APRÈS réalisation des hémocultures

• En période néonatale: ajouter **Amoxicilline 50 mg/kg**

**TDM AVANT TOUTE PL SI:**

• Signes de localisation neurologique
• Troubles de la vigilance associés à:
  - Anomalie pupillaire
  - Crise tonique postérieure
  - Aréactivité
  - Dysautonomie
  - Réaction de décortication ou décérébration
• Crises convulsives récentes ou en cours
• Signes d'engagement (mydriase unilatérale, HTA)

**SI LCR TROUBLE À LA PL:**

• En urgence:
  - Hémocultures
  - Antibiothérapie
  - Dexaméthasone d'emblée

**ÉLÉMENTS D'ORIENTATION MÉNINGITE BACTÉRIENNE**

**LCR évocateur:**
• GB: 1000-5000 cellules/mm³
• 80-95% PNN
• Glycorachie < 0,4 g/L (50-60% des cas)
• Glycorachie/Glycémie ≤ 0,4
• Protéinorachie: 1 à 5 g/L
• **Lactate LCR:**
  - Si < 3,2 mmol/L → MB très peu probable
• **PCT sérique:**
  - Si < 0,5 ng/mL → MB très peu probable

**TRANSPORT**

• Médicalisé vers service adapté
• Surveillance continue

**À SAVOIR**

• Ne JAMAIS retarder l'antibiothérapie pour attendre un examen complémentaire
• Dexaméthasone AVANT ou AVEC première dose d'antibiotique
• Hémocultures avant ATB si possible (sans retarder le traitement)

**DÉFINITION:**
Infection bactérienne des méninges constituant une urgence vitale absolue.

**TABLEAU CLINIQUE:**

**Triade classique (60% des cas):**
• Fièvre > 38°C
• Céphalées intenses
• Raideur de nuque

**Signes de gravité:**
• Troubles de conscience (Glasgow < 14)
• Convulsions
• Signes de localisation neurologique
• Purpura (évoque méningocoque)
• Instabilité hémodynamique

**Syndrome méningé:**
• Céphalées violentes, holocrâniennes
• Photophobie, phonophobie
• Nausées, vomissements
• Raideur de nuque (signe de Kernig)
• Position en "chien de fusil"

**GERMES PRINCIPAUX:**
• **Pneumocoque** (40-50%)
• **Méningocoque** (25%)
• **Listeria** (si > 50 ans ou immunodéprimé)

**ANTIBIOTHÉRAPIE URGENTE (< 90 min):**

**AVANT TOUT EXAMEN COMPLÉMENTAIRE!**

**Adulte sain < 50 ans:**
• **Ceftriaxone 2g IV** lente (4 min)
  OU
• **Céfotaxime 300 mg/kg/j** en 4 fois (max 12g/j)

**Adulte > 50 ans ou immunodéprimé:**
• **Ceftriaxone 2g IV** 
  +
• **Amoxicilline 3g IV** toutes les 4h (anti-Listeria)

**Si allergie C3G:**
• **Vancomycine 15 mg/kg IV** (1g en 1h)
  +
• **Rifampicine 10 mg/kg IV** (max 600 mg)

**CORTICOTHÉRAPIE ADJUVANTE:**
• **Dexaméthasone 10 mg IV**
• AVANT ou AVEC 1ère dose d'antibiotique
• Poursuivre 4 jours si pneumocoque confirmé
• Indication: Suspicion de pneumocoque

**PONCTION LOMBAIRE:**

**Indications:**
• Confirmer le diagnostic
• Identifier le germe
• Adapter l'antibiothérapie

**Contre-indications absolues:**
• Suspicion HTIC sévère:
  - Glasgow < 11
  - Convulsions
  - Déficit neurologique focal
  - Œdème papillaire au FO
• Troubles de l'hémostase
• Infection cutanée point de ponction
• Instabilité hémodynamique sévère

**EN PRÉ-HOSPITALIER:**
• **PL NON SYSTÉMATIQUE**
• **Ne JAMAIS retarder l'antibiothérapie**
• PL hospitalière si état clinique le permet

**TRAITEMENT SYMPTOMATIQUE:**

**Si convulsions:**
• **Diazépam 10 mg IV** lente
  OU
• **Midazolam 5-10 mg IV** ou IM

**Si HTIC suspectée:**
• Position demi-assise 30°
• **Mannitol 20% 250 mL IV** en 20 min
  OU
• **SSH 7,5% 150 mL** en 15 min

**Antiémétiques:**
• **Ondansétron 4-8 mg IV**

**SURVEILLANCE:**
• État de conscience (Glasgow)
• Température
• PA, FC, SpO2
• Recherche signes HTIC
• Apparition purpura

**PROPHYLAXIE DE L'ENTOURAGE:**

**Si méningocoque confirmé ou suspecté:**
• Déclaration obligatoire ARS
• Contact infectiologue/SAMU
• Rifampicine pour contacts proches

**Équipe de secours:**
• **Rifampicine 600 mg PO** x 2/j x 2j
  OU
• **Ciprofloxacine 500 mg PO** dose unique

**ORIENTATION:**
• **Réanimation ou USC SYSTÉMATIQUE**
• Transport médicalisé
• Monitoring continu
• Information hôpital d'accueil

**POINTS CLÉS:**
✓ Antibiothérapie IV IMMÉDIATE (< 90 min)
✓ Ne JAMAIS retarder pour scanner ou PL
✓ Dexaméthasone AVANT antibiotiques
✓ Traiter comme méningocoque si purpura
✓ Surveillance neurologique rapprochée`
    },
    {
      id: 'purpura-fulminans',
      name: 'Purpura Fulminans',
      icon: AlertTriangle,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      border: 'border-orange-400/30',
      content: `**PURPURA FULMINANS**

**DIAGNOSTIC**

**Définition:**
• Choc septique avec purpura extensif
• Au moins un élément nécrotique ou ecchymotique de plus de 3 mm de diamètre
• Ne disparaissant pas à la vitropression
• Confluent, prédominant aux membres

**Épidémiologie:**
• Première cause de mortalité infectieuse chez l'enfant
• 500 cas/an en France
• 25% de mortalité

**Examen clinique:**
• Examen cutané exhaustif
• Déshabiller entièrement le patient (y compris siège)
• ⚠ Attention: Pas de syndrome méningé forcément associé

**ÉLÉMENTS DE GRAVITÉ**

• Âge < 1 an
• Retard diagnostic, antibiothérapie débutée tardivement
• Purpura rapidement extensif
• Intensité de l'ischémie des extrémités, SpO2 non mesurable
• Défaillance cardiorespiratoire
• Convulsions, obnubilation, coma

**CONDUITE À TENIR**

**Mesures immédiates:**
• Patient allongé
• **2 VVP** (recours rapide à l'IO si échec)
  - 1 VVP glucosé
  - 1 VVP NaCl/remplissage
• Scope monitoring
• Surveillance de la température
• Surveillance de la glycémie ++
• O2 nasal pour SpO2 > 90%
• Hémocultures si disponible

**Perfusion de base:**
• **PG5** sur la base de 1800 mL/m²/24h:
  - 40 mL/h si 10 kg
  - 50 mL/h si 15 kg
  - 60 mL/h si 20 kg

**ANTIBIOTHÉRAPIE**
**⏱ LA PLUS PRÉCOCE POSSIBLE**
**À DOSE MÉNINGÉE**

• **C3G 50 mg/kg**
  - **Ceftriaxone** (de préférence, max 2g)
  - OU **Céfotaxime**
• Par voie **IV** ou **IO** ou **IM**

**REMPLISSAGE ET AMINES:**
• Cf protocole choc septique pédiatrique
• Remplissage vasculaire rapide
• Noradrénaline si hypotension persistante

**ORIENTATION**

• **Réanimation pédiatrique du CHU**
• Après régulation SAMU
• Transport médicalisé urgent

**À SAVOIR**

• **Contre-indication PL** avant résultats de coagulation
• Faire ATB AVANT PL
• Chaque minute compte
• Pronostic vital et fonctionnel engagé
• Risque d'amputations

**PROPHYLAXIE DE L'ENTOURAGE**

Si méningocoque confirmé ou suspecté:
• Déclaration obligatoire ARS
• Contact infectiologue/SAMU
• Rifampicine pour contacts proches

**Équipe de secours:**
• **Rifampicine 600 mg PO** x 2/j x 2j
  OU
• **Ciprofloxacine 500 mg PO** dose unique

**DÉFINITION:**
Forme septicémique gravissime de méningococcémie avec purpura extensif et choc septique.

**URGENCE VITALE ABSOLUE - Pronostic vital engagé**

**DIAGNOSTIC CLINIQUE:**

**Purpura:**
• Lésions purpuriques **nécrotiques**
• **Extensives** (≥ 3 mm)
• **Rapidement évolutives** (nouvelles lésions en quelques heures)
• Ne s'effaçant PAS à la vitropression
• Prédominance membres inférieurs initialement
• Évolution possible vers placards ecchymotiques

**Signes de choc septique:**
• Hypotension (PAS < 90 mmHg)
• Tachycardie > 100 bpm
• Marbrures, extrémités froides
• Trouble de conscience
• Oligurie

**TRAITEMENT IMMÉDIAT:**

**⏱ TEMPS = PRONOSTIC**

**1. ANTIBIOTHÉRAPIE URGENTE (délai < 30 min):**

**Sur place, AVANT hospitalisation:**
• **Ceftriaxone 2g IV** directe (4 min)
  - Ampoule 1g: administrer 2 ampoules
  - Si pas de VVP: **IM** acceptable
  
**Alternative si pas d'accès veineux:**
• **Ceftriaxone 1g IM** dans chaque fesse (2g total)

**Si allergie bétalactamines:**
• **Ciprofloxacine 400 mg IV**

**2. RÉANIMATION HÉMODYNAMIQUE:**

**Remplissage vasculaire massif:**
• **NaCl 0,9%** ou **Ringer Lactate**
• 20-30 mL/kg en 30 min (soit 1,5-2L)
• Répéter si nécessaire

**Vasopresseurs si hypotension persistante:**
• **Noradrénaline** 0,1-1 µg/kg/min
  - Dilution: 4 mg dans 50 mL
  - Objectif: PAM ≥ 65 mmHg
  - Sur VVP ou intra-osseuse si nécessaire

**3. CORTICOTHÉRAPIE:**
• **Hydrocortisone 200 mg IV** en bolus
• Puis 50 mg toutes les 6h

**4. OXYGÉNOTHÉRAPIE:**
• Masque haute concentration
• Objectif: SpO2 > 94%
• Intubation si signes de détresse respiratoire

**5. TRAITEMENT SYMPTOMATIQUE:**

**Convulsions:**
• **Diazépam 10 mg IV** ou **Midazolam 5 mg IV/IM**

**Fièvre > 40°C:**
• **Paracétamol 1g IV**

**6. ABORD VEINEUX:**
• **2 VVP** de gros calibre minimum
• **Voie intra-osseuse** si échec VVP
• Prélèvements: hémocultures AVANT antibiotiques (si possible sans retard)

**SURVEILLANCE:**
• PA, FC, SpO2 continu
• État de conscience (Glasgow)
• Extension du purpura (photos)
• Diurèse
• Température
• Glycémie capillaire

**MESURES ASSOCIÉES:**

**Isolement:**
• Précautions gouttelettes
• Masque chirurgical patient
• Protection équipe soignante

**Déclaration:**
• **Déclaration obligatoire immédiate** ARS
• Information entourage proche

**ORIENTATION:**
• **Réanimation URGENTE**
• Pré-alerte systématique
• Transport médicalisé scope
• Maintien remplissage durant transport

**PROPHYLAXIE CONTACTS:**

**Contacts proches < 48h:**
• **Rifampicine 600 mg PO** x 2/j x 2j
  OU
• **Ciprofloxacine 500 mg PO** dose unique

**Équipe de secours:**
• Prophylaxie systématique

**CRITÈRES DE GRAVITÉ:**
• Purpura extensif > 10% surface corporelle
• Choc réfractaire au remplissage
• Troubles de conscience
• Insuffisance respiratoire
• CIVD (purpura confluent, saignements)

**POINTS CLÉS:**
✓ Ceftriaxone IV IMMÉDIATE (même sans voie veineuse → IM)
✓ Remplissage vasculaire massif
✓ Noradrénaline précoce si choc
✓ Transport réanimation URGENT
✓ Chaque minute compte: MORTALITÉ 10-40%`
    },
    {
      id: 'covid-detresse-respi',
      name: 'Détresse Respiratoire - Virus Émergent/COVID',
      icon: Wind,
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-400/30',
      content: `**PRISE EN CHARGE DÉTRESSE RESPIRATOIRE AIGUË**
**INFECTION VIRUS ÉMERGENT - COVID-19**

**MESURES GÉNÉRALES**

**Protection de l'équipe:**
• **Tenues soignants adaptées au risque**
• **Filtre sur circuit de ventilation et BAVU**
• **Filtres antibactériens** sur valves inspiratoires et expiratoires du respirateur
• **Minimiser le risque lié aux aérosols et gouttelettes ++**

**Précautions:**
• **Pas d'utilisation de VNI** hors indications franches:
  - OAP
  - Décompensation de BPCO
  - Pré-oxygénation avant intubation
• Risque de dissémination virale
• **Limiter au maximum les déconnexions** du respirateur

**PRÉ-OXYGÉNATION**

**Oxygène haute concentration ou VNI:**

Si pré-oxygénation VNI (patient très hypoxémique):
⚠ À éviter au maximum du fait du risque de dissémination du virus

**Procédure VNI si nécessaire:**
• Appliquer le masque en s'assurant de son **étanchéité AVANT** de démarrer le ventilateur
• Ventiler avec **pressions modérées** pour éviter les fuites
  - Par exemple: AI 10, PEP 5
  - Pendant 3 à 5 minutes
• **Arrêter la ventilation AVANT** le retrait du masque

**INTUBATION**

**Intubation séquence rapide classique:**

• Utilisation en **première intention du vidéo-laryngoscope**
  - Si opérateur formé et performant avec le matériel
  - Sinon: laryngoscopie directe classique

• **Obturation de la sonde** d'intubation par nez artificiel de trachéotomie

**VENTILATION MÉCANIQUE**

**Démarrage ventilation:**
• Démarrer la ventilation mécanique en pression positive APRÈS:
  - Gonflage du ballonnet de la sonde d'intubation
  - Raccord du circuit fermé du ventilateur
• Vérification de la pression du ballonnet pour éviter les fuites
• Cf protocole de ventilation

**Sédation:**
• **Au mieux:**
  - **Propofol/Sufentanil**
  - OU **Midazolam/Sufentanil**
  - Doses habituelles (cf fiches médicaments)

**Curarisation:**
• **Curarisation rapide** après intubation
• Après mise en route de la sédation pour éviter la toux

**Protocole:**
• **Bolus Atracurium 0,5 mg/kg**
• Puis **IVSE Atracurium 0,5 mg/kg/h**

**Surveillance:**
• Limiter les aspirations sur sonde
• Sauf obstruction majeure ou circuit fermé

**CHANGEMENT DE RESPIRATEUR SI NÉCESSAIRE:**

**Procédure sécurisée:**
1. Mettre le ventilateur en **pause** (au mieux pause expiratoire)
2. **Clamper la sonde**
3. Déconnecter la sonde en **laissant le filtre en place**
4. Connecter le nouveau respirateur
5. Mettre en route la ventilation

**DÉSINFECTION POST-PROCÉDURE**

• **Tout le matériel non protégé** par filtre hydrophobe utilisé pour la ventilation → jeté ou désinfecté
• Matériel pour l'intubation → jeté ou désinfecté
• **Détergent désinfectant standard** pour:
  - Manche du vidéo-laryngoscope
  - Lame du vidéo-laryngoscope
• Aération des locaux
• Surfaces contact → virucide

**POINTS CLÉS**

✓ Protection équipe = PRIORITÉ absolue
✓ EPI complets systématiques
✓ Éviter VNI si possible (aérosols)
✓ Vidéo-laryngoscope en première intention
✓ Filtres sur tous les circuits
✓ Limiter déconnexions respirateur
✓ Curarisation précoce après intubation
✓ Désinfection complète matériel`
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl" data-testid="referentiels-infectio-container">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-400/30">
            <Shield className="w-8 h-8 text-red-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
              Référentiels Infectiologie
            </h1>
            <p className="text-[hsl(var(--muted-2))] mt-1">
              Protocoles urgences infectieuses - SMUR 2025
            </p>
          </div>
        </div>

        {/* Alert Antibiotiques Précoces */}
        <Alert className="border-orange-400/50 bg-orange-500/10">
          <AlertTriangle className="h-5 w-5 text-orange-400" />
          <AlertDescription className="text-orange-300 font-medium">
            <strong>Golden Hour Infectiologie:</strong> Antibiothérapie précoce = Pronostic vital • Choc septique: ATB &lt; 1h • Méningite: ATB &lt; 90 min • Purpura fulminans: ATB &lt; 30 min
          </AlertDescription>
        </Alert>
      </div>

      {/* Protocols Card */}
      <Card className="border-[hsl(var(--border))] bg-[hsl(var(--bg-elev))]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Activity className="w-6 h-6 text-red-400" />
            Protocoles Infectiologiques
          </CardTitle>
          <CardDescription>
            Sélectionnez un protocole pour afficher les recommandations détaillées
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="space-y-4">
            {protocols.map((protocol) => {
              const Icon = protocol.icon;
              const isLoading = aiLoading[protocol.id];
              const aiResult = aiResults[protocol.id];

              return (
                <AccordionItem 
                  key={protocol.id} 
                  value={protocol.id}
                  className={`border ${protocol.border} ${protocol.bg} rounded-lg overflow-hidden`}
                  data-testid="protocol-item"
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline">
                    <div className="flex items-center gap-3 text-left">
                      <div className={`p-2 rounded-lg ${protocol.bg} border ${protocol.border}`}>
                        <Icon className={`w-5 h-5 ${protocol.color}`} />
                      </div>
                      <span className="font-semibold text-lg">{protocol.name}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="px-6 pb-6 space-y-6">
                      <ScrollArea className="h-auto max-h-[600px]">
                        <div className="prose prose-invert max-w-none pr-4">
                          <div className="text-[hsl(var(--text))] space-y-4">
                            {protocol.content.split('\n').map((line, idx) => {
                              if (line.startsWith('**') && line.endsWith('**')) {
                                const text = line.replace(/\*\*/g, '');
                                if (line.includes('DIAGNOSTIC') || line.includes('TRAITEMENT') || line.includes('CONDUITE')) {
                                  return <h3 key={idx} className="text-xl font-bold text-cyan-400 mt-6 mb-3">{text}</h3>;
                                }
                                return <h4 key={idx} className="text-lg font-semibold text-cyan-300 mt-4 mb-2">{text}</h4>;
                              }
                              if (line.trim().startsWith('•')) {
                                return <li key={idx} className="ml-4 text-[hsl(var(--text))] leading-relaxed">{line.replace('•', '').trim()}</li>;
                              }
                              if (line.trim().startsWith('-')) {
                                return <li key={idx} className="ml-8 text-[hsl(var(--muted-2))] text-sm leading-relaxed list-disc">{line.replace('-', '').trim()}</li>;
                              }
                              if (line.trim().startsWith('✓')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-3 bg-cyan-500/10 rounded-lg border border-cyan-400/30 my-2">
                                    <span className="text-cyan-300 font-medium">{line.replace('✓', '').trim()}</span>
                                  </div>
                                );
                              }
                              if (line.trim().startsWith('⚠')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-3 bg-orange-500/10 rounded-lg border border-orange-400/30 my-2">
                                    <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-orange-300 font-medium">{line.replace('⚠', '').trim()}</span>
                                  </div>
                                );
                              }
                              if (line.trim().startsWith('⏱')) {
                                return (
                                  <div key={idx} className="flex items-center gap-2 p-3 bg-red-500/10 rounded-lg border border-red-400/30 my-2">
                                    <Activity className="w-5 h-5 text-red-400 flex-shrink-0" />
                                    <span className="text-red-300 font-bold text-lg">{line.replace('⏱', '').trim()}</span>
                                  </div>
                                );
                              }
                              if (line.trim()) {
                                return <p key={idx} className="text-[hsl(var(--text))] leading-relaxed">{line}</p>;
                              }
                              return <div key={idx} className="h-2" />;
                            })}
                          </div>
                        </div>
                      </ScrollArea>

                      <Button
                        onClick={() => handleAiAnalysis(protocol.id, protocol.name, protocol.content)}
                        disabled={isLoading}
                        className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                        data-testid={`ai-button-${protocol.id}`}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Analyse AI en cours...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Analyse AI de ce protocole
                          </>
                        )}
                      </Button>

                      {aiResult && (
                        <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-400/30 rounded-lg">
                          <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                            <Sparkles className="w-5 h-5" />
                            Analyse AI
                          </h3>
                          <div className="prose prose-invert max-w-none">
                            <div className="text-[hsl(var(--text))] whitespace-pre-wrap text-sm" data-testid={`ai-result-${protocol.id}`}>
                              {aiResult}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}
